import {EngineConfig_Media} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_Media.js";
import {LiveSession, TriggerPackage} from "../../LiveSession.js";
import {EngineSessionComp} from "./EngineSessionComp.js";
import {AudioFileEntry} from "../../../Utils/Bridge/Bridge_Native/MediaTypes.js";
import {WebAudioPlayer, webAudio_forcedPan} from "../../../Utils/Media/WebAudioPlayer.js";
import {PlaySoundOpts} from "../../../Utils/Bridge/Bridge_Native/MediaPlayer.js";
import {SoundPanningType} from "../../../Server/DB/@Shared/EffectPointer.js";
import {observable} from "mobx";
import {minuteInMS, RunInAction, RunInAction_Set, secondInMS} from "web-vcore";
import {GetRandomNumber, Timer} from "js-vextensions";
import {MediaTimer} from "../../../Utils/Media/MediaTimer.js";
import {PLACEHOLDER_TIMER_INTERVAL} from "../../../Utils/LibIntegrations/MobXGraphlink.js";
import {VisualizationComp} from "./VisualizationComp.js";
import {BackgroundComp} from "./BackgroundComp.js";

const POSITION_UPDATE_INTERVAL_MS = 500;

export class MediaComp extends EngineSessionComp<EngineConfig_Media> {
	constructor(session: LiveSession, config: EngineConfig_Media) {
		super(session, config, s=>config.enabled, s=>s.IsLocal());
	}

	/** Currently active WebAudioPlayer instance (null when stopped). */
	player: WebAudioPlayer|null = null;
	private positionUpdateTimer = new Timer(POSITION_UPDATE_INTERVAL_MS, ()=>this._UpdatePositionInStore()).SetContext(this.s.timerContext);

	// Media time: total ms of audio playback since session start; advances only while a track plays.
	private mediaTime_accumulated: number = 0;
	/** While media is playing, this is set; once media stops, the duration here gets added to mediaTime_accumulated. */
	private mediaTime_lastStart: number|null = null;

	// desync-period-related state
	// ==========

	private desync_nextStartTimer = new MediaTimer(PLACEHOLDER_TIMER_INTERVAL, ()=>this._ActivateDesync(), 1)
		.SetMediaTimerOptions(()=>this.GetMediaTime()).SetContext(this.s.timerContext);

	/** This timer creates delayed entries in the pendingPanChanges array, whenever the realtime pan-direction changes. */
	private desync_pollTimer = new Timer(30, ()=>this._DesyncPanPoll()).SetContext(this.s.timerContext);
	/** Queue of pan-direction changes waiting to be applied, each with an absolute wall-clock fire-time. */
	private desync_pendingPanChanges: Array<{pan: "left"|"right"|null, fireAt: number}> = [];
	/** Drainer runs every 30 ms (real time) and applies any queue entries whose fireAt has passed. */
	private desync_pendingPanDrainer = new Timer(30, ()=>{
		const now = Date.now();
		while (this.desync_pendingPanChanges.length > 0 && this.desync_pendingPanChanges[0].fireAt <= now) {
			const {pan} = this.desync_pendingPanChanges.shift()!;
			this.desync_currentPlayerPan = pan;
			if (this.player) this.player.forcedPanOverride = pan;
		}
		if (this.desync_pendingPanChanges.length === 0) this.desync_pendingPanDrainer.Stop();
	}).SetContext(this.s.timerContext);

	private desync_startedAt: number|null = null;
	/** Media time (ms) when the current desync period began; used for mediaTimeSinceDesyncStart. */
	private desync_startedAt_mediaTime: number|null = null;
	private desync_lastSeenPan: "left"|"right"|null = null;
	/** The pan value currently applied to the player (may be stale/delayed during a desync period). */
	private desync_currentPlayerPan: "left"|"right"|null = null;

	// ui state
	// ==========

	uiState = observable({
		/** Index into config.mediaPools for the currently selected pool. */
		currentPoolIndex: 0,
		/** The audio file currently loaded/playing. */
		selectedFile: null as AudioFileEntry|null,
		isPlaying: false,
		playbackSpeed: 1,
		volumeMultiplier: 1,
		/** Current playback position, in seconds. Updated every 500 ms. */
		currentPosition: 0,
		/** Duration of the loaded file, in seconds. */
		duration: 0,
		/** True while a desync training period is active (pan is intentionally delayed). */
		desync_isActive: false,
	});

	// remainder
	// ==========

	/** Returns the currently-selected MediaPool based on the panel's pool index, or null. */
	get currentPool() {
		return this.c.mediaPools?.[this.uiState.currentPoolIndex] ?? null;
	}

	GetTriggerPackages() {
		return [
			new TriggerPackage("Media_TogglePlaying", this.c.togglePlaying_triggerSet, this, {}, ()=>this.TogglePlaying()),
			new TriggerPackage("Media_JumpForward", this.c.jumpForward_triggerSet, this, {}, ()=>this.JumpForward()),
			new TriggerPackage("Media_JumpBackward", this.c.jumpBackward_triggerSet, this, {}, ()=>this.JumpBackward()),
			new TriggerPackage("Media_IncreasePlaySpeed", this.c.increasePlaySpeed_triggerSet, this, {}, ()=>this.IncreasePlaySpeed()),
			new TriggerPackage("Media_DecreasePlaySpeed", this.c.decreasePlaySpeed_triggerSet, this, {}, ()=>this.DecreasePlaySpeed()),
			new TriggerPackage("Media_MarkDesyncCaught", this.c.markDesyncCaught_triggerSet, this, {}, ()=>this.MarkDesyncCaught()),
		];
	}

	override OnActivate(sessionStarting: boolean) {
		// Periodically sync audio-element position to the store so the UI slider stays live.
		this.positionUpdateTimer.Start();
		this._ScheduleNextDesync();
	}

	override OnDeactivate(sessionStopping: boolean) {
		this.positionUpdateTimer.Stop();
		this._StopDesync();
		RunInAction("MediaComp.OnDeactivate", ()=>this.uiState.desync_isActive = false);
		this.StopPlayer();
	}

	// ---- public API (called by MediaPanel and trigger packages) ----

	Play() {
		// if no player is set up yet, this must be the first (or selected file was changed); start new player
		if (this.player == null) {
			const file = this.uiState.selectedFile;
			if (file == null) return;
			this._StartPlayer(file);
			this.s.AsLocal!.AddEvent({type: "Media.Play"});
			return;
		}

		// resume a paused player
		this.player.Resume();
		this._SetIsPlaying(true);
		this.s.AsLocal!.AddEvent({type: "Media.Play"});
	}
	Pause() {
		this.player?.Pause();
		this._SetIsPlaying(false);
		this.s.AsLocal!.AddEvent({type: "Media.Pause"});
	}

	TogglePlaying() {
		if (this.uiState.isPlaying) this.Pause();
		else this.Play();
	}

	JumpForward() {
		const el = this.player?.audioElement;
		if (el) {
			el.currentTime = Math.min(el.currentTime + this.c.jumpSeconds, isFinite(el.duration) ? el.duration : el.currentTime + this.c.jumpSeconds);
			this._UpdatePositionInStore();
		}
	}
	JumpBackward() {
		const el = this.player?.audioElement;
		if (el) {
			el.currentTime = Math.max(el.currentTime - this.c.jumpSeconds, 0);
			this._UpdatePositionInStore();
		}
	}

	IncreasePlaySpeed() {
		const newSpeed = (this.uiState.playbackSpeed + this.c.speedStep).RoundTo(.01);
		this.SetPlaybackSpeed(newSpeed);
	}
	DecreasePlaySpeed() {
		const newSpeed = Math.max(0.25, (this.uiState.playbackSpeed - this.c.speedStep).RoundTo(.01));
		this.SetPlaybackSpeed(newSpeed);
	}
	SetPlaybackSpeed(speed: number) {
		RunInAction("MediaComp.SetPlaybackSpeed", ()=>this.uiState.playbackSpeed = speed);
		if (this.player?.audioElement) {
			this.player.audioElement.playbackRate = speed;
		}
	}

	SetVolumeMultiplier(multiplier: number) {
		RunInAction("MediaComp.SetVolumeMultiplier", ()=>this.uiState.volumeMultiplier = multiplier);
		this.UpdateVolume();
	}
	GetNewFinalVolume() {
		return (this.currentPool?.volume ?? 0.05) * this.uiState.volumeMultiplier;
	}
	UpdateVolume() {
		if (this.player) {
			// send "false", to inform SetVolume that the volume still needs the global volume-effects applied
			this.player.SetVolume(this.GetNewFinalVolume(), false);
		}
	}

	Seek(seconds: number) {
		const el = this.player?.audioElement;
		if (el) {
			el.currentTime = seconds;
			this._UpdatePositionInStore();
		}
	}

	/** Called when the user notices the pan is out of sync with their breathing. Logs the event,
	 *  ends the current desync period, and schedules the next one. */
	MarkDesyncCaught() {
		if (this.uiState.desync_isActive && this.desync_startedAt != null && this.desync_startedAt_mediaTime != null) {
			const timeSinceDesyncStart = (Date.now() - this.desync_startedAt) / 1000;
			const mediaTimeSinceDesyncStart = (this.GetMediaTime() - this.desync_startedAt_mediaTime) / 1000;
			this._StopDesync();
			RunInAction_Set(()=>this.uiState.desync_isActive = false);
			this.s.AsLocal!.AddEvent({type: "Media.MarkDesyncCaught", media_markDesyncCaught: {timeSinceDesyncStart, mediaTimeSinceDesyncStart}});
			this._ScheduleNextDesync();
		} else {
			// User signaled desync-caught when no desync was active; log with nulls to indicate mistaken signal
			this.s.AsLocal!.AddEvent({type: "Media.MarkDesyncCaught", media_markDesyncCaught: {timeSinceDesyncStart: null, mediaTimeSinceDesyncStart: null}});
		}
	}

	/** Load a new file. If playback was active, restarts with the new file. */
	SelectFile(file: AudioFileEntry) {
		const wasPlaying = this.uiState.isPlaying;
		this.StopPlayer();
		RunInAction("MediaComp.SelectFile", ()=>{
			this.uiState.selectedFile = file;
			this.uiState.currentPosition = 0;
			// AudioFileEntry.duration is in ms; use it as an initial estimate (refined once audio metadata loads)
			this.uiState.duration = file.duration > 0 ? file.duration / 1000 : 0;
		});
		if (wasPlaying) {
			void this._StartPlayer(file);
		}
	}

	StopPlayer() {
		this.player?.StopAndCleanup();
		this.player = null;
		RunInAction("MediaComp.StopPlayer", ()=>this.uiState.currentPosition = 0);
		this._SetIsPlaying(false);
	}

	// ---- private helpers ----

	private async _StartPlayer(file: AudioFileEntry) {
		const speed = this.uiState.playbackSpeed;

		this.player = new WebAudioPlayer({
			contentUri: file.contentUri,
			playOpts: new PlaySoundOpts({
				volume: this.GetNewFinalVolume(),
				loop: false,
				// if desync-training is enabled, user definitely wants simple panning, so set it (else, use global fallback)
				panningType: this.c.desync_enabled ? SoundPanningType.simple : undefined,
			}).Finalized(),
		});

		await this.player.Start();
		// If a desync period is already running, apply the current stale pan to the new player
		// so it doesn't briefly snap to the live global pan before the next poll tick.
		if (this.uiState.desync_isActive) {
			this.player.forcedPanOverride = this.desync_currentPlayerPan;
		}
		this.UpdateVolume();

		// _ApplyPlaybackRate skips when speed_multiplier_min/max are 0 — set rate directly.
		if (this.player.audioElement && speed !== 1) {
			this.player.audioElement.playbackRate = speed;
		}

		RunInAction("MediaComp._StartPlayer", ()=>{
			if (file.duration > 0) this.uiState.duration = file.duration / 1000;
		});
		this._SetIsPlaying(true);

		// Refine duration from actual audio metadata once it loads
		if (this.player.audioElement) {
			const syncDuration = ()=>{
				const dur = this.player?.audioElement?.duration;
				if (dur != null && isFinite(dur) && dur > 0) {
					RunInAction("MediaComp._StartPlayer.duration", ()=>this.uiState.duration = dur);
				}
			};
			if (isFinite(this.player.audioElement.duration) && this.player.audioElement.duration > 0) {
				syncDuration();
			} else {
				this.player.audioElement.addEventListener("loadedmetadata", syncDuration, {once: true});
			}

			// Clear playing state when the file ends naturally
			const capturedPlayer = this.player;
			this.player.audioElement.addEventListener("ended", ()=>{
				// Only clear if this is still the active player (user may have loaded a new file)
				if (this.player === capturedPlayer) {
					RunInAction("MediaComp.ended", ()=>this.uiState.currentPosition = 0);
					this._SetIsPlaying(false);
					this.player = null;
				}
			}, {once: true});
		}
	}

	// ---- desync training helpers ----

	/** Returns total milliseconds of audio playback elapsed since the session started.
	 *  Advances only while a track is playing; frozen while paused or stopped.
	 *  Similar to Date.now() but counting only time spent with audio playing. */
	GetMediaTime(): number {
		return this.mediaTime_accumulated + (this.mediaTime_lastStart != null ? Date.now() - this.mediaTime_lastStart : 0);
	}

	/** Sets isPlaying and updates the media-time clock so that GetMediaTime() only advances while a track is actually playing.
	 * All (non-transient) desync-period-related timers use MediaTimer which polls GetMediaTime(), so they auto-pause with the audio. */
	private _SetIsPlaying(nowPlaying: boolean) {
		RunInAction("MediaComp.SetIsPlaying", ()=>this.uiState.isPlaying = nowPlaying);
		this.s.Comp(BackgroundComp).UpdateEffectsEnabled();
		this.s.Comp(VisualizationComp).UpdatePromptingEnabled();
		if (nowPlaying && this.mediaTime_lastStart == null) {
			this.mediaTime_lastStart = Date.now();
		} else if (!nowPlaying && this.mediaTime_lastStart != null) {
			this.mediaTime_accumulated += Date.now() - this.mediaTime_lastStart;
			this.mediaTime_lastStart = null;
		}
	}

	private _ScheduleNextDesync() {
		const c = this.c;
		if (!c.desync_enabled) return;
		const maxTime = c.desync_startTimeMax ?? 0;
		if (maxTime <= 0) return;
		const minTime = c.desync_startTimeMin ?? 0;
		const delayMs = GetRandomNumber({min: minTime, max: maxTime}) * minuteInMS;
		// MediaTimer only advances while GetMediaTime() advances (i.e. while audio is playing),
		// so no play-state guard is needed here.
		this.desync_nextStartTimer.Start(delayMs);
	}

	private _ActivateDesync() {
		this.desync_startedAt = Date.now();
		this.desync_startedAt_mediaTime = this.GetMediaTime();
		this.desync_lastSeenPan = webAudio_forcedPan;
		this.desync_currentPlayerPan = webAudio_forcedPan;
		if (this.player) this.player.forcedPanOverride = webAudio_forcedPan;
		RunInAction("MediaComp._ActivateDesync", ()=>this.uiState.desync_isActive = true);
		this.desync_pollTimer.Start();
	}

	private _DesyncPanPoll() {
		const currentPan = webAudio_forcedPan;
		if (currentPan === this.desync_lastSeenPan) return;
		this.desync_lastSeenPan = currentPan;
		const c = this.c;
		const delayMs = GetRandomNumber({min: (c.desync_panDelayMin ?? 0), max: (c.desync_panDelayMax ?? 0)}) * secondInMS;
		this.desync_pendingPanChanges.push({pan: currentPan, fireAt: Date.now() + delayMs});
		if (!this.desync_pendingPanDrainer.Enabled) this.desync_pendingPanDrainer.Start();
	}

	private _StopDesync() {
		this.desync_nextStartTimer.Stop();
		this.desync_pollTimer.Stop();
		this.desync_pendingPanDrainer.Stop();
		this.desync_pendingPanChanges = [];
		this.desync_startedAt = null;
		this.desync_startedAt_mediaTime = null;
		this.desync_lastSeenPan = null;
		this.desync_currentPlayerPan = null;
		// Resume live global pan
		if (this.player) this.player.forcedPanOverride = undefined;
	}

	private _UpdatePositionInStore() {
		const el = this.player?.audioElement;
		if (el) {
			const pos = el.currentTime;
			const dur = el.duration;
			RunInAction("MediaComp._UpdatePositionInStore", ()=>{
				this.uiState.currentPosition = isFinite(pos) ? pos : 0;
				if (isFinite(dur) && dur > 0) this.uiState.duration = dur;
			});
		}
	}
}